import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { hasNonEmptyValue, hasValue } from 'Lib/Any';
import { pipe } from 'Lib/Hooks/Selectors';
import React from 'react';
import { useSelector } from 'react-redux';
import deepEquals from '../../../../../Lib/Object/deepEquals';
import { Checkbox, Field, NumericInput, TextOutput } from '../../../../Core';
import { withInjectedProps } from '../../../../Core/InjectedPropsContext';
import {
  WorksheetEquipmentDropdown,
  WorksheetEquipmentStatus,
  WorksheetGridFieldSection,
  WorksheetRecordGrid,
} from '../../../Core';

let AsCompactionDataSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const minTestPortions = 3;

  const useSameMouldForAllTestPortions =
    useSelector(createWorksheetSelector!('Equipment.UseMouldForAllTestPortions')) ?? false;

  /**
   * A collection of properties we need to select from each test portion
   * to render the record
   */
  const testPortionRecordProps = useSelector(
    pipe(createWorksheetSelector!('TestPortions'), (portions: Array<any>) =>
      (portions ?? []).map((p: any) => ({
        dim: p.ExcludedPoint,

        showMouldStatusButton:
          (hasNonEmptyValue(p.Mould?.Status) && !p.Mould.Status.InCalibration) || p.MouldCalibrationMismatch,

        lockMould: useSameMouldForAllTestPortions,
        lockMouldMass: useSameMouldForAllTestPortions || hasValue(p.Mould?.Mass),
        lockMouldVolume: useSameMouldForAllTestPortions || hasValue(p.Mould?.Volume),

        // We need to obtain the full mould for each record as the status button requires it :/
        mould: p.Mould,
        mouldMass: p.MouldMass,
        mouldVolume: p.MouldVolume,
        mouldCalibrationMismatch: p.MouldCalibrationMismatch,
      }))
    ),
    deepEquals
  );

  return (
    <WorksheetGridFieldSection heading="Compaction Data" name="TestPortions">
      <WorksheetRecordGrid
        name={'TestPortions'}
        orientation={'horizontal'}
        rowHeaderWidth={200}
        recordProps={{
          width: 130,
          dim: (i) => testPortionRecordProps[i]?.dim,
          heading: 'number',
          headingLabel: 'Test Portion ID',
          preventRemove: testPortionRecordProps.length <= minTestPortions,
          removeConfirm: {
            confirm: true,
            confirmTitle: 'Portion Deletion',
            confirmText: 'Are you sure you want to delete the selected portion?',
            confirmButtonText: 'Delete Portion',
          },
        }}
        fieldSet={[
          {
            name: '',
            component: (i) => (
              <WorksheetEquipmentStatus
                equipmentType="density-mould"
                value={testPortionRecordProps[i].mould}
                warningIconOnly
                importedCalibration={{
                  Mass: testPortionRecordProps[i].mouldMass,
                  Volume: testPortionRecordProps[i].mouldVolume,
                  Mismatch: testPortionRecordProps[i].mouldCalibrationMismatch,
                }}
                importModel={{
                  Id: testPortionRecordProps[i].mould?.Id,
                }}
              />
            ),
            hidden: useSameMouldForAllTestPortions || !testPortionRecordProps.some((p) => p.showMouldStatusButton),
          },
          {
            name: 'Mould',
            component: (i) =>
              testPortionRecordProps[i].lockMould ? (
                <Field input={<TextOutput />} value={testPortionRecordProps[i].mould?.Code} />
              ) : (
                <Field
                  input={<WorksheetEquipmentDropdown />}
                  equipmentType="density-mould"
                  placeholder="Select..."
                  disabled={useSameMouldForAllTestPortions}
                />
              ),
          },
          {
            name: 'MouldMass',
            component: (i) => (
              <Field input={testPortionRecordProps[i].lockMouldMass ? <TextOutput /> : <NumericInput />} />
            ),
          },
          {
            name: 'MouldVolume',
            component: (i) => (
              <Field input={testPortionRecordProps[i].lockMouldVolume ? <TextOutput /> : <NumericInput />} />
            ),
          },
          { name: 'PortionMass', component: <Field input={<NumericInput />} /> },
          {
            name: 'WaterAddedTargetPercent',
            component: <Field input={<NumericInput />} />,
          },
          { name: 'WaterAddedTarget', component: <Field input={<TextOutput />} /> },
          {
            name: 'PortionMassAfterWaterAdded',
            component: <Field input={<NumericInput />} />,
          },
          { name: 'WaterAdded', component: <Field input={<TextOutput />} /> },
          { name: 'WaterAddedPercent', component: <Field input={<TextOutput />} /> },
          { name: 'WetSoilAndMouldMass', component: <Field input={<NumericInput />} /> },
          { name: 'WetSoilMass', component: <Field input={<TextOutput />} /> },
          { name: 'WetDensity', component: <Field input={<TextOutput />} /> },
          { name: 'ConvertedWetDensity', component: <Field input={<TextOutput />} /> },
          {
            name: 'ExcludedPoint',
            component: <Field input={<Checkbox />} />,
            label: 'Exclude Point',
          },
        ]}
      />
    </WorksheetGridFieldSection>
  );
};

AsCompactionDataSection = withInjectedProps(AsCompactionDataSection);

export { AsCompactionDataSection };
export default AsCompactionDataSection;
